<script setup lang="ts">
  import { Stepper, Typo, TypoVariant, Divider } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import type { TouristsSelection } from '@/entities/searchCriteria';
  import RoomCard from './RoomCard.vue';
  import { TOURISTS_TEXTS } from '../config/texts';
  import { MAX_ROOMS, MIN_ROOMS, normalizeTourists } from '../lib/touristsRooms';
  import { useTouristsRooms } from '../model/useTouristsRooms';

  interface TouristsRoomsPanelProps {
    modelValue?: TouristsSelection | null;
    isVisibleAddButton?: boolean;
  }

  const props = withDefaults(defineProps<TouristsRoomsPanelProps>(), {
    modelValue: null,
    isVisibleAddButton: true,
  });

  interface TouristsRoomsPanelEmits {
    (e: 'update:modelValue', sel: TouristsSelection): void;
    (e: 'open-age-picker', payload: { roomIndex: number }): void;
  }

  const emit = defineEmits<TouristsRoomsPanelEmits>();

  const model = computed<TouristsSelection>({
    get: () => normalizeTourists(props.modelValue),
    set: (value: TouristsSelection) => emit('update:modelValue', value),
  });

  const core = useTouristsRooms({ model, isVisibleAddButton: () => props.isVisibleAddButton });

  const roomsCount = computed<number>({
    get: () => core.rooms.value.length,
    set: (next) => {
      const current = core.rooms.value.length;
      if (next > current) {
        core.addRoom();
      } else if (next < current) {
        core.removeRoom(current - 1);
      }
    },
  });

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<template>
  <div
    class="tourists-rooms-panel"
    data-testid="tourists-rooms-panel"
  >
    <div
      v-if="isVisibleAddButton"
      class="tourists-rooms-panel__count"
    >
      <Typo
        :variant="TypoVariant.BodyLRegular"
        tag="span"
      >
        {{ TOURISTS_TEXTS.roomsCountLabel }}
      </Typo>
      <Stepper
        v-model="roomsCount"
        :min="MIN_ROOMS"
        :max="MAX_ROOMS"
        data-testid="tourists-rooms-count"
      />
    </div>

    <Divider type="dashed" />

    <div class="tourists-rooms-panel__rooms">
      <div
        v-for="(room, i) in core.rooms.value"
        :key="i"
        class="tourists-rooms-panel__room-wrap"
      >
        <RoomCard
          :room="room"
          :index="i"
          :mobile="true"
          :full-width="true"
          :removable="core.canRemoveRoom.value"
          :age-options="core.ageOptions"
          :is-visible-header="isVisibleAddButton"
          @update:adults="core.setAdults(i, $event)"
          @add-child="core.addChild(i, $event)"
          @change-child-age="core.setChildAge(i, $event.childIndex, $event.age)"
          @remove-child="core.removeChild(i, $event)"
          @remove-room="core.removeRoom(i)"
          @open-age-picker="emit('open-age-picker', { roomIndex: i })"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tourists-rooms-panel {
    color: var(--color-text-primary);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
    width: 100%;

    &__count {
      align-items: center;
      display: flex;
      justify-content: space-between;
    }

    &__rooms {
      display: flex;
      flex-direction: column;
    }

    &__room-wrap {
      padding-top: var(--spacing-l);

      &:first-child {
        padding-top: 0;
      }

      &:not(:first-child) {
        border-top: 1px dashed var(--color-disabled-500);
        margin-top: var(--spacing-l);
      }
    }
  }
</style>
